/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  ChangeEmailRequest,
  ConfirmEmailRequest,
  DeleteAdminV1MeSessionsIdPathParameters,
  DeleteAdminV1MeToursParams,
  EmailChanged,
  ForgotPasswordRequest,
  GetAdminV1AuthInvitationsTokenPathParameters,
  GoogleCallbackRequest,
  GoogleStart,
  GoogleStartRequest,
  InvitationPreview,
  MagicLinkRequest,
  MagicLinkVerifyRequest,
  PostAdminV1AuthInvitationsAcceptBody,
  PostAdminV1AuthLogoutBody,
  PostAdminV1AuthRefreshBody,
  PutAdminV1MeToursTourIdBody,
  PutAdminV1MeToursTourIdPathParameters,
  ResetPasswordRequest,
  RevokedSessions,
  SignInProviders,
  StaffChangePasswordRequest,
  StaffLoginRequest,
  StaffProfile,
  StaffSessionList,
  SwitchOrganizationRequest,
  TokenResponse,
  TourProgress,
  TourProgressList,
  TwoFactorChallenge,
  TwoFactorCodeRequest,
  TwoFactorDisableRequest,
  TwoFactorEnabled,
  TwoFactorSetup,
  TwoFactorStatus,
  TwoFactorVerifyRequest,
  UpdateMeRequest
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * Send the challenge token with a 6-digit authenticator code or one of the recovery codes. Answers like login; a recovery code is struck out once used. 401 UNAUTHORIZED for a wrong code, 401 TOKEN_EXPIRED once the challenge's expiresAt has passed (sign in again), 401 TOKEN_INVALID for a tampered or foreign challenge. Wrong codes count against the login limit of 10 per minute per IP, which answers 429 with Retry-After
 * @summary Finish a login that answered 202 with a two-factor challenge
 */
export const postAdminV1Auth2faVerify = (
    twoFactorVerifyRequest: BodyType<TwoFactorVerifyRequest>,
 options?: SecondParameter<typeof customInstance<TokenResponse>>,) => {
      return customInstance<TokenResponse>(
      {url: `/admin/v1/auth/2fa/verify`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: twoFactorVerifyRequest
    },
      options);
    }
  /**
 * @summary Two-factor status of the signed-in account
 */
export const getAdminV1Auth2fa = (

 options?: SecondParameter<typeof customInstance<TwoFactorStatus>>,) => {
      return customInstance<TwoFactorStatus>(
      {url: `/admin/v1/auth/2fa`, method: 'GET'
    },
      options);
    }
  /**
 * The secret is pending until /auth/2fa/enable confirms a code from the app. 409 while two-factor is already enabled
 * @summary Start enabling two-factor: a new secret and its otpauth URL for the authenticator app
 */
export const postAdminV1Auth2faSetup = (

 options?: SecondParameter<typeof customInstance<TwoFactorSetup>>,) => {
      return customInstance<TwoFactorSetup>(
      {url: `/admin/v1/auth/2fa/setup`, method: 'POST'
    },
      options);
    }
  /**
 * Answers with ten recovery codes shown once; Nucleus keeps only their hashes
 * @summary Confirm the authenticator and switch two-factor on
 */
export const postAdminV1Auth2faEnable = (
    twoFactorCodeRequest: BodyType<TwoFactorCodeRequest>,
 options?: SecondParameter<typeof customInstance<TwoFactorEnabled>>,) => {
      return customInstance<TwoFactorEnabled>(
      {url: `/admin/v1/auth/2fa/enable`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: twoFactorCodeRequest
    },
      options);
    }
  /**
 * @summary Replace the recovery codes
 */
export const postAdminV1Auth2faRecoveryCodes = (
    twoFactorCodeRequest: BodyType<TwoFactorCodeRequest>,
 options?: SecondParameter<typeof customInstance<TwoFactorEnabled>>,) => {
      return customInstance<TwoFactorEnabled>(
      {url: `/admin/v1/auth/2fa/recovery-codes`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: twoFactorCodeRequest
    },
      options);
    }
  /**
 * 409 TWO_FACTOR_REQUIRED_BY_ORGANIZATION while an organization the person belongs to (or the platform, for operators) requires two-factor; meta.organizations names them
 * @summary Switch two-factor off with the password and a live code
 */
export const postAdminV1Auth2faDisable = (
    twoFactorDisableRequest: BodyType<TwoFactorDisableRequest>,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/auth/2fa/disable`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: twoFactorDisableRequest
    },
      options);
    }
  /**
 * Always 202. The link points at the app's /magic-link page with ?token= (staff: plus &organization= when organizationSlug was given); single use, 15 minutes
 * @summary Email a one-time sign-in link
 */
export const postAdminV1AuthMagicLink = (
    magicLinkRequest: BodyType<MagicLinkRequest>,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/auth/magic-link`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: magicLinkRequest
    },
      options);
    }
  /**
 * Answers like login: 200 with tokens, or 202 with a two-factor challenge when the account has two-factor on
 * @summary Exchange a magic link token for a session
 */
export const postAdminV1AuthMagicLinkVerify = (
    magicLinkVerifyRequest: BodyType<MagicLinkVerifyRequest>,
 options?: SecondParameter<typeof customInstance<TokenResponse | TwoFactorChallenge>>,) => {
      return customInstance<TokenResponse | TwoFactorChallenge>(
      {url: `/admin/v1/auth/magic-link/verify`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: magicLinkVerifyRequest
    },
      options);
    }
  /**
 * @summary Which sign-in methods this surface offers, for the sign-in page
 */
export const getAdminV1AuthProviders = (

 options?: SecondParameter<typeof customInstance<SignInProviders>>,) => {
      return customInstance<SignInProviders>(
      {url: `/admin/v1/auth/providers`, method: 'GET'
    },
      options);
    }
  /**
 * Answers the Google authorization URL with a signed state. Google returns the person to the app's /auth/google/callback page, which posts code and state to /auth/google/callback. 409 GOOGLE_SIGN_IN_UNAVAILABLE when the platform has no Google client
 * @summary Where to send the browser to sign in with Google
 */
export const postAdminV1AuthGoogleStart = (
    googleStartRequest: BodyType<GoogleStartRequest>,
 options?: SecondParameter<typeof customInstance<GoogleStart>>,) => {
      return customInstance<GoogleStart>(
      {url: `/admin/v1/auth/google/start`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: googleStartRequest
    },
      options);
    }
  /**
 * Answers like login: 200 with tokens and the session cookie, or 202 with a two-factor challenge. 403 when no account uses the Google email (accounts come from invitations, never from Google), 401 TOKEN_INVALID when Google refused the code, 422 for a bad or expired state
 * @summary Finish Google sign-in
 */
export const postAdminV1AuthGoogleCallback = (
    googleCallbackRequest: BodyType<GoogleCallbackRequest>,
 options?: SecondParameter<typeof customInstance<TokenResponse | TwoFactorChallenge>>,) => {
      return customInstance<TokenResponse | TwoFactorChallenge>(
      {url: `/admin/v1/auth/google/callback`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: googleCallbackRequest
    },
      options);
    }
  /**
 * For API clients that received `refreshToken` at login (requests without the X-Nucleus-Client header). The presented token is revoked and a new one issued; a token that was already rotated or revoked answers 401. Browser apps keep using the cookie and never call this
 * @summary Exchange a refresh token for a new access token and refresh token
 */
export const postAdminV1AuthRefresh = (
    postAdminV1AuthRefreshBody: BodyType<PostAdminV1AuthRefreshBody>,
 options?: SecondParameter<typeof customInstance<TokenResponse>>,) => {
      return customInstance<TokenResponse>(
      {url: `/admin/v1/auth/refresh`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1AuthRefreshBody
    },
      options);
    }
  /**
 * @summary Change your name or preferred locale
 */
export const patchAdminV1Me = (
    updateMeRequest: BodyType<UpdateMeRequest>,
 options?: SecondParameter<typeof customInstance<StaffProfile>>,) => {
      return customInstance<StaffProfile>(
      {url: `/admin/v1/me`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateMeRequest
    },
      options);
    }
  /**
 * `organizations` lists every organization the person belongs to, most recently used first. `supportSession` is set when a platform operator is looking in read-only
 * @summary Current staff user, organization, permissions and stores
 */
export const getAdminV1Me = (

 options?: SecondParameter<typeof customInstance<StaffProfile>>,) => {
      return customInstance<StaffProfile>(
      {url: `/admin/v1/me`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Your live sessions on every device and organization
 */
export const getAdminV1MeSessions = (

 options?: SecondParameter<typeof customInstance<StaffSessionList>>,) => {
      return customInstance<StaffSessionList>(
      {url: `/admin/v1/me/sessions`, method: 'GET'
    },
      options);
    }
  /**
 * Ending the current session signs this client out too
 * @summary Sign out one of your sessions
 */
export const deleteAdminV1MeSessionsId = (
    { id }: DeleteAdminV1MeSessionsIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/me/sessions/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Ends every live session of yours except the one making this request
 * @summary Sign out everywhere else
 */
export const postAdminV1MeSessionsRevokeOthers = (

 options?: SecondParameter<typeof customInstance<RevokedSessions>>,) => {
      return customInstance<RevokedSessions>(
      {url: `/admin/v1/me/sessions/revoke-others`, method: 'POST'
    },
      options);
    }
  /**
 * Needs the current password. Every other session is signed out and a notice is emailed; this session stays
 * @summary Change your password
 */
export const postAdminV1MePassword = (
    staffChangePasswordRequest: BodyType<StaffChangePasswordRequest>,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/me/password`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: staffChangePasswordRequest
    },
      options);
    }
  /**
 * Needs the current password. A confirmation link goes to the new address (1 hour, single use); nothing changes until POST /auth/email/confirm. 409 ALREADY_EXISTS when another account uses it
 * @summary Start changing your email address
 */
export const postAdminV1MeEmailChange = (
    changeEmailRequest: BodyType<ChangeEmailRequest>,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/me/email/change`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: changeEmailRequest
    },
      options);
    }
  /**
 * Works signed in or out: the link proves the new mailbox is yours. Sign in with the new address from now on
 * @summary Finish an email change with the token from the confirmation link
 */
export const postAdminV1AuthEmailConfirm = (
    confirmEmailRequest: BodyType<ConfirmEmailRequest>,
 options?: SecondParameter<typeof customInstance<EmailChanged>>,) => {
      return customInstance<EmailChanged>(
      {url: `/admin/v1/auth/email/confirm`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: confirmEmailRequest
    },
      options);
    }
  /**
 * Returns a Bearer token for API clients and sets the `nucleus_staff` HttpOnly session cookie for browser apps. Answers 202 with a challenge when the account has two-factor on; finish at /auth/2fa/verify. Without organizationSlug the organization used most recently is chosen. 403 INVITATION_PENDING (meta.email) when the person's only memberships are invitations they have not accepted
 * @summary Staff login with email and password
 */
export const postAdminV1AuthLogin = (
    staffLoginRequest: BodyType<StaffLoginRequest>,
 options?: SecondParameter<typeof customInstance<TokenResponse | TwoFactorChallenge>>,) => {
      return customInstance<TokenResponse | TwoFactorChallenge>(
      {url: `/admin/v1/auth/login`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: staffLoginRequest
    },
      options);
    }
  /**
 * Revokes the session behind the `nucleus_staff` cookie and clears it; an API client passes its refresh token in the body instead. Safe to call without either
 * @summary End the browser session
 */
export const postAdminV1AuthLogout = (
    postAdminV1AuthLogoutBody: BodyType<PostAdminV1AuthLogoutBody>,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/auth/logout`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1AuthLogoutBody
    },
      options);
    }
  /**
 * Issues a new session and token for the chosen organization (one of GET /me `organizations`), the same envelope as login; the second factor completed at sign-in carries over. 404 when you are not an active member there
 * @summary Move this sign-in to another organization you belong to
 */
export const postAdminV1AuthSwitchOrganization = (
    switchOrganizationRequest: BodyType<SwitchOrganizationRequest>,
 options?: SecondParameter<typeof customInstance<TokenResponse>>,) => {
      return customInstance<TokenResponse>(
      {url: `/admin/v1/auth/switch-organization`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: switchOrganizationRequest
    },
      options);
    }
  /**
 * Always answers 202. The email goes out only when an active staff account has that address; the link works once and expires in 15 minutes
 * @summary Email a password reset link
 */
export const postAdminV1AuthPasswordForgot = (
    forgotPasswordRequest: BodyType<ForgotPasswordRequest>,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/auth/password/forgot`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: forgotPasswordRequest
    },
      options);
    }
  /**
 * Ends every existing session of the account
 * @summary Set a new password with a reset link
 */
export const postAdminV1AuthPasswordReset = (
    resetPasswordRequest: BodyType<ResetPasswordRequest>,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/auth/password/reset`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: resetPasswordRequest
    },
      options);
    }
  /**
 * @summary Preview an invitation before accepting it
 */
export const getAdminV1AuthInvitationsToken = (
    { token }: GetAdminV1AuthInvitationsTokenPathParameters,
 options?: SecondParameter<typeof customInstance<InvitationPreview>>,) => {
      return customInstance<InvitationPreview>(
      {url: `/admin/v1/auth/invitations/${token}`, method: 'GET'
    },
      options);
    }
  /**
 * A person without a password chooses one here and is signed in. A person who already has an account must be signed in as that account (Bearer or cookie) to accept: otherwise 401 INVITATION_REQUIRES_LOGIN with meta.email, and `password` in the body is refused (422). An account with two-factor on must have signed in with it (403 TWO_FACTOR_REQUIRED). Returns the same token and cookie as login, for the organization just joined
 * @summary Accept an invitation and sign in to that organization
 */
export const postAdminV1AuthInvitationsAccept = (
    postAdminV1AuthInvitationsAcceptBody: BodyType<PostAdminV1AuthInvitationsAcceptBody>,
 options?: SecondParameter<typeof customInstance<TokenResponse>>,) => {
      return customInstance<TokenResponse>(
      {url: `/admin/v1/auth/invitations/accept`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1AuthInvitationsAcceptBody
    },
      options);
    }
  /**
 * The same in every organization the person belongs to. A support session reads an empty list
 * @summary Guided-tour progress of the signed-in person
 */
export const getAdminV1MeTours = (

 options?: SecondParameter<typeof customInstance<TourProgressList>>,) => {
      return customInstance<TourProgressList>(
      {url: `/admin/v1/me/tours`, method: 'GET'
    },
      options);
    }
  /**
 * Without tourId every tour is offered again; with it only that one
 * @summary Reset guided-tour progress
 */
export const deleteAdminV1MeTours = (
    params?: DeleteAdminV1MeToursParams,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/me/tours`, method: 'DELETE',
        params
    },
      options);
    }
  /**
 * Upsert on the tour id: the latest version and status win. At most 300 tours per person
 * @summary Record a tour as completed or dismissed
 */
export const putAdminV1MeToursTourId = (
    { tourId }: PutAdminV1MeToursTourIdPathParameters,
    putAdminV1MeToursTourIdBody: BodyType<PutAdminV1MeToursTourIdBody>,
 options?: SecondParameter<typeof customInstance<TourProgress>>,) => {
      return customInstance<TourProgress>(
      {url: `/admin/v1/me/tours/${tourId}`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: putAdminV1MeToursTourIdBody
    },
      options);
    }
  export type PostAdminV1Auth2faVerifyResult = NonNullable<Awaited<ReturnType<typeof postAdminV1Auth2faVerify>>>
export type GetAdminV1Auth2faResult = NonNullable<Awaited<ReturnType<typeof getAdminV1Auth2fa>>>
export type PostAdminV1Auth2faSetupResult = NonNullable<Awaited<ReturnType<typeof postAdminV1Auth2faSetup>>>
export type PostAdminV1Auth2faEnableResult = NonNullable<Awaited<ReturnType<typeof postAdminV1Auth2faEnable>>>
export type PostAdminV1Auth2faRecoveryCodesResult = NonNullable<Awaited<ReturnType<typeof postAdminV1Auth2faRecoveryCodes>>>
export type PostAdminV1Auth2faDisableResult = NonNullable<Awaited<ReturnType<typeof postAdminV1Auth2faDisable>>>
export type PostAdminV1AuthMagicLinkResult = NonNullable<Awaited<ReturnType<typeof postAdminV1AuthMagicLink>>>
export type PostAdminV1AuthMagicLinkVerifyResult = NonNullable<Awaited<ReturnType<typeof postAdminV1AuthMagicLinkVerify>>>
export type GetAdminV1AuthProvidersResult = NonNullable<Awaited<ReturnType<typeof getAdminV1AuthProviders>>>
export type PostAdminV1AuthGoogleStartResult = NonNullable<Awaited<ReturnType<typeof postAdminV1AuthGoogleStart>>>
export type PostAdminV1AuthGoogleCallbackResult = NonNullable<Awaited<ReturnType<typeof postAdminV1AuthGoogleCallback>>>
export type PostAdminV1AuthRefreshResult = NonNullable<Awaited<ReturnType<typeof postAdminV1AuthRefresh>>>
export type PatchAdminV1MeResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1Me>>>
export type GetAdminV1MeResult = NonNullable<Awaited<ReturnType<typeof getAdminV1Me>>>
export type GetAdminV1MeSessionsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1MeSessions>>>
export type DeleteAdminV1MeSessionsIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1MeSessionsId>>>
export type PostAdminV1MeSessionsRevokeOthersResult = NonNullable<Awaited<ReturnType<typeof postAdminV1MeSessionsRevokeOthers>>>
export type PostAdminV1MePasswordResult = NonNullable<Awaited<ReturnType<typeof postAdminV1MePassword>>>
export type PostAdminV1MeEmailChangeResult = NonNullable<Awaited<ReturnType<typeof postAdminV1MeEmailChange>>>
export type PostAdminV1AuthEmailConfirmResult = NonNullable<Awaited<ReturnType<typeof postAdminV1AuthEmailConfirm>>>
export type PostAdminV1AuthLoginResult = NonNullable<Awaited<ReturnType<typeof postAdminV1AuthLogin>>>
export type PostAdminV1AuthLogoutResult = NonNullable<Awaited<ReturnType<typeof postAdminV1AuthLogout>>>
export type PostAdminV1AuthSwitchOrganizationResult = NonNullable<Awaited<ReturnType<typeof postAdminV1AuthSwitchOrganization>>>
export type PostAdminV1AuthPasswordForgotResult = NonNullable<Awaited<ReturnType<typeof postAdminV1AuthPasswordForgot>>>
export type PostAdminV1AuthPasswordResetResult = NonNullable<Awaited<ReturnType<typeof postAdminV1AuthPasswordReset>>>
export type GetAdminV1AuthInvitationsTokenResult = NonNullable<Awaited<ReturnType<typeof getAdminV1AuthInvitationsToken>>>
export type PostAdminV1AuthInvitationsAcceptResult = NonNullable<Awaited<ReturnType<typeof postAdminV1AuthInvitationsAccept>>>
export type GetAdminV1MeToursResult = NonNullable<Awaited<ReturnType<typeof getAdminV1MeTours>>>
export type DeleteAdminV1MeToursResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1MeTours>>>
export type PutAdminV1MeToursTourIdResult = NonNullable<Awaited<ReturnType<typeof putAdminV1MeToursTourId>>>
